import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ExistingPhieuThuData, InvoiceSource, PhieuThuFormInitialData } from '../types';
import { PhieuThuInput, type AccountModel } from '@/types/schemas';
import { transformDataFromHoaDon } from '../utils/transform-data';
import { useRouter, useSearchParams } from '@/routing';
import { useAuth } from '@/contexts/auth-context';
import { QUERY_KEYS } from '@/constants';
import { useCRUD } from '@/hooks';
import api from '@/lib/api';

export function useInvoiceParamsPresence() {
  const searchParams = useSearchParams();
  return useMemo(() => Boolean(searchParams.get('hdbh') || searchParams.get('hdbdv')), [searchParams]);
}

interface UseMultipleFormsArgs {
  addPhieuThu: (data: PhieuThuInput) => Promise<unknown>;
  editPhieuThu?: (uuid: string, data: PhieuThuInput) => Promise<unknown>;
}

export function useMultipleForms({ addPhieuThu, editPhieuThu }: UseMultipleFormsArgs) {
  const searchParams = useSearchParams();
  const router = useRouter();
  const { entity } = useAuth();

  const hasInvoiceParams = useInvoiceParamsPresence();

  // Get mode parameter from URL
  const mode = searchParams.get('mode');

  const [invoiceData, setInvoiceData] = useState<InvoiceSource[]>([]);
  const [showMultipleForms, setShowMultipleForms] = useState<boolean>(hasInvoiceParams);
  const [currentFormIndex, setCurrentFormIndex] = useState<number>(0);
  const [existingPhieuThuData, setExistingPhieuThuData] = useState<ExistingPhieuThuData[]>([]);

  // Fetch tai khoan for default tk_data in initial form data
  const taiKhoanSearchData = useMemo(() => ({ prefix: '1111' }), []);
  const { data: taiKhoan, isLoading: isLoadingTaiKhoan } = useCRUD<AccountModel, Partial<AccountModel>>({
    endpoint: QUERY_KEYS.TAI_KHOAN,
    searchData: taiKhoanSearchData
  });

  // Function to fetch invoice data by UUID
  const fetchInvoiceByUuid = useCallback(
    async (uuid: string, isHoaDonBanHang: boolean = false) => {
      if (!entity?.slug) return null;

      try {
        const endpoint = isHoaDonBanHang ? QUERY_KEYS.HOA_DON_BAN_HANG : QUERY_KEYS.HOA_DON_BAN_DICH_VU;
        const response = await api.get<InvoiceSource>(`/entities/${entity.slug}/erp/${endpoint}/${uuid}/`);
        return response.data;
      } catch (error) {
        return null;
      }
    },
    [entity?.slug]
  );

  // Function to fetch existing phiếu thu data by UUID
  const fetchPhieuThuByUuid = useCallback(
    async (uuid: string) => {
      if (!entity?.slug) return null;

      try {
        const response = await api.get<ExistingPhieuThuData>(
          `/entities/${entity.slug}/erp/${QUERY_KEYS.PHIEU_THU}/${uuid}/`
        );
        return response.data;
      } catch (error) {
        return null;
      }
    },
    [entity?.slug]
  );

  // Handle URL params for creating phieu thu from invoices
  useEffect(() => {
    const hdbhParam = searchParams.get('hdbh');
    const hdbdvParam = searchParams.get('hdbdv');

    if (hdbhParam || hdbdvParam) {
      setShowMultipleForms(true);

      const invoiceUuids = (hdbhParam || hdbdvParam)?.split(',').filter(Boolean) || [];
      const isHoaDonBanHang = !!hdbhParam;

      if (invoiceUuids.length > 0) {
        Promise.all(invoiceUuids.map(uuid => fetchInvoiceByUuid(uuid, isHoaDonBanHang)))
          .then(async invoices => {
            const validInvoices = invoices.filter((invoice): invoice is InvoiceSource => Boolean(invoice));
            setInvoiceData(validInvoices);

            // If in edit mode, also fetch existing phiếu thu data
            if (mode === 'edit' && validInvoices.length > 0) {
              const phieuThuPromises = validInvoices.map(invoice => {
                const phieuThuUuid = invoice.phieu_thu_uuids?.[0];
                return phieuThuUuid ? fetchPhieuThuByUuid(phieuThuUuid) : null;
              });

              const existingPhieuThus = await Promise.all(phieuThuPromises);
              setExistingPhieuThuData(
                existingPhieuThus.filter((phieuThu): phieuThu is ExistingPhieuThuData => Boolean(phieuThu))
              );
            }

            if (validInvoices.length > 0) {
              setCurrentFormIndex(0);
            }
          })
          .catch(error => {
            console.error('Error fetching invoices:', error);
          });
      }
    }
  }, [searchParams, entity?.slug, fetchInvoiceByUuid, fetchPhieuThuByUuid, mode]);

  // Multiple forms submit handler
  const handleMultipleFormSubmit = useCallback(
    async (data: PhieuThuInput) => {
      try {
        if (mode === 'edit' && editPhieuThu) {
          // In edit mode, we need to get the UUID from the current invoice data
          const currentInvoice = invoiceData[currentFormIndex];
          const existingPhieuThu = existingPhieuThuData[currentFormIndex];

          if (currentInvoice?.phieu_thu_uuids?.[0] && existingPhieuThu) {
            // Include old phiếu thu data in the submission
            const editDataWithOldValues = {
              ...data,
              // Include old values from existing phiếu thu
              i_so_ct: existingPhieuThu.i_so_ct,
              so_ct: existingPhieuThu.so_ct,
              ma_nk: existingPhieuThu.ma_nk,
              ngay_ct: existingPhieuThu.ngay_ct,
              ngay_lct: existingPhieuThu.ngay_lct
            };

            await editPhieuThu(currentInvoice.phieu_thu_uuids[0], editDataWithOldValues);
          }
        } else {
          await addPhieuThu(data);
        }

        if (currentFormIndex < invoiceData.length - 1) {
          setShowMultipleForms(true);
          setCurrentFormIndex(currentFormIndex + 1);
        } else {
          setShowMultipleForms(false);
          setInvoiceData([]);
          setCurrentFormIndex(0);
          setExistingPhieuThuData([]);
          const hdbhParam = searchParams.get('hdbh');
          if (hdbhParam) {
            router.push('/ban-hang/hoa-don-ban-ra/hoa-don-ban-hang');
          } else {
            router.back();
          }
        }
      } catch (error) {
        return;
      }
    },
    [addPhieuThu, editPhieuThu, mode, currentFormIndex, invoiceData, existingPhieuThuData, router, searchParams]
  );

  // Close handler for multiple forms
  const handleCloseMultipleForms = useCallback(() => {
    setShowMultipleForms(false);
    setInvoiceData([]);
    setCurrentFormIndex(0);
    setExistingPhieuThuData([]);
  }, []);

  // Build initial form data from current invoice + default tk_data
  let initialData: Partial<PhieuThuFormInitialData> = useMemo(
    () => transformDataFromHoaDon(invoiceData[currentFormIndex]),
    [invoiceData, currentFormIndex]
  );

  if (!isLoadingTaiKhoan && taiKhoan && taiKhoan.length > 0) {
    initialData = {
      ...initialData,
      tk_data: taiKhoan[0]
    };
  }

  // If in edit mode, merge with existing phiếu thu data
  if (mode === 'edit' && existingPhieuThuData[currentFormIndex]) {
    const existingPhieuThu = existingPhieuThuData[currentFormIndex];
    initialData = {
      ...initialData,
      i_so_ct: existingPhieuThu.i_so_ct,
      so_ct: existingPhieuThu.so_ct,
      ma_nk: existingPhieuThu.ma_nk,
      ngay_ct: existingPhieuThu.ngay_ct,
      ngay_lct: existingPhieuThu.ngay_lct
    };
  }

  return {
    hasInvoiceParams,
    showMultipleForms,
    invoiceData,
    currentFormIndex,
    initialData,
    handleMultipleFormSubmit,
    handleCloseMultipleForms,
    formMode: mode === 'edit' ? 'edit' : 'add'
  } as const;
}
